import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { enAU } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { toast } from "react-toastify";
import "../styles/BookingsDetails.css";

export default function BookingsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [eventInclusionsText, setEventInclusionsText] = useState([]);
  const [additionsText, setAdditionsText] = useState([]);
  const [additionalNotesText, setAdditionalNotesText] = useState([]);
  const [loading, setLoading] = useState(false);

  const bookingatPhone = (phone) => {
    if (!phone) return "";

    let cleaned = phone.replace(/\D/g, "");

    if (cleaned.startsWith("61")) {
      cleaned = "0" + cleaned.slice(2);
    } else if (cleaned.startsWith("+61")) {
      cleaned = "0" + cleaned.slice(3);
    }

    if (cleaned.length === 10 && cleaned.startsWith("04")) {
      return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, "$1 $2 $3");
    }

    if (/^0[2-8]\d{8}$/.test(cleaned)) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2 $3");
    }

    // Fallback: NOT starting with 0 → 4 + 3 grouping
    if (!cleaned.startsWith("0")) {
      const first = cleaned.slice(0, 4);
      const rest = cleaned.slice(4);

      const groupedRest = rest.match(/\d{1,4}/g)?.join(" ") || "";

      return [first, groupedRest].filter(Boolean).join(" ");
    }
    return cleaned;
  };

  // Helper function to restrict time for datepicker minTime and maxTime
  const BRISBANE_TZ = "Australia/Brisbane";

  // Helper function to restrict time for datepicker minTime and maxTime
  const setTime = (hour, minutes = 0) => {
    const now = new Date();
    const wallClock = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minutes,
      0,
      0,
    );
    return fromZonedTime(wallClock, BRISBANE_TZ);
  };

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/bookings/${id}`,
          {
            credentials: "include",
          },
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch booking details");
        }

        setBooking({
          ...data.booking,
          picnicDate: data.booking.picnicDate
            ? new Date(data.booking.picnicDate)
            : null,
        });
        setEventInclusionsText(
          (data.booking.eventInclusions || []).map((a) => `${a}`).join("\n"),
        );

        setAdditionsText(
          (data.booking.additions || [])
            .map((a) => `${a.name}: ${a.quantity} @ ${a.price}`)
            .join("\n"),
        );

        setAdditionalNotesText(
          (data.booking.additionalNotes || []).map((a) => `${a}`).join("\n"),
        );
      } catch (err) {
        toast.error(err.message || "Failed to fetch booking details");
        setBooking(null);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  useEffect(() => {
    if (!booking) return;

    setEventInclusionsText((booking.eventInclusions || []).join("\n"));

    setAdditionalNotesText((booking.additionalNotes || []).join("\n"));

    setAdditionsText(
      (booking.additions || [])
        .map((a) => `${a.name}: ${a.quantity} @ ${a.price}`)
        .join("\n"),
    );
  }, [booking]);

  const handleChange = (field, value) => {
    setBooking((prev) => {
      const updated = { ...prev, [field]: value };

      const inclusionsCost = Number(updated.inclusionsCost) || 0;
      const additionsCost = Number(updated.additionsCost) || 0;
      const paidCost = Number(updated.paidCost) || 0;

      const totalCost = inclusionsCost + additionsCost;
      const balanceCost = totalCost - paidCost;

      updated.totalCost = totalCost;
      updated.balanceCost = balanceCost;

      return {
        ...updated,
      };
    });
  };

  const saveBooking = async () => {
    if (!booking) return null;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/bookings/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(booking),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update booking");
      }

      console.log("savebooking data: ", data.booking);

      toast.success("Booking updated successfully!");

      return data.booking;
    } catch (err) {
      toast.error(err.message || "Failed to update booking");
    }
  };

  const handleUpdate = async () => {
    try {
      await saveBooking();

      navigate("/dashboard/bookings");
    } catch (err) {
      toast.error(err.message || "Failed to update booking");
    }
  };

  const handleCreateInvoice = async () => {
    try {
      const updateBooking = await saveBooking();

      navigate(`/dashboard/bookings/invoice/${updateBooking._id}`);
    } catch (err) {
      toast.error(err.message || "failed to save booking");
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this booking?",
    );
    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/bookings/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to delete booking");

      toast.success("Booking deleted successfully!");

      navigate("/dashboard/bookings");
    } catch (err) {
      toast.error(err.message || "Failed to delete booking");
    }
  };

  const handlePrintInvoice = async () => {
    window.print();
  };

  if (loading) {
    return <div>Loading booking details......</div>;
  }

  if (!booking) {
    return <div>Booking not found</div>;
  }

  const additionsCostDisplay =
    booking.additionsCost === 0 ? "" : booking.additionsCost;
  const paidCostDisplay = booking.paidCost === 0 ? "" : booking.paidCost;
  const inclusionsCostDisplay =
    booking.inclusionsCost === 0 ? "" : booking.inclusionsCost;

  return (
    <div className="bookingDetail__container">
      <h2>Booking Details : {booking.name}</h2>

      <div className="bookingDetail__details">
        <label>Invoice Number: </label>
        <input
          type="text"
          value={booking.invoiceNumber || ""}
          onChange={(e) => handleChange("invoiceNumber", e.target.value)}
        />
        <label>Name: </label>
        <input
          type="text"
          value={booking.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
        />
        <label>Email: </label>
        <input
          type="email"
          value={booking.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
        <label>Phone:</label>
        <input
          type="tel"
          value={bookingatPhone(booking.phone)}
          onChange={(e) => handleChange("phone", e.target.value)}
        />
        <label>Picnic Date:</label>
        <DatePicker
          selected={booking.picnicDate}
          onChange={(date) => handleChange("picnicDate", date)}
          shouldCloseOnSelect
          locale={enAU}
          timeZone={BRISBANE_TZ}
          dateFormat="dd/MM/yyyy h:mm aa"
          showTimeSelect
          timeIntervals={15}
          minTime={setTime(6)}
          maxTime={setTime(18)}
        />
        <label>Preferred Location:</label>
        <input
          type="text"
          value={booking.preferredLocation || ""}
          onChange={(e) => handleChange("preferredLocation", e.target.value)}
        />
        <label>Picnic Setting:</label>
        <input
          type="text"
          value={booking.picnicSetting || ""}
          onChange={(e) => handleChange("picnicSetting", e.target.value)}
        />
        <label>Picnic Style:</label>
        <input
          type="text"
          value={booking.picnicStyle || ""}
          onChange={(e) => handleChange("picnicStyle", e.target.value)}
        />
        <label>Celebration Type:</label>
        <input
          type="text"
          value={booking.celebrationType || ""}
          onChange={(e) => handleChange("celebrationType", e.target.value)}
        />
        <label>Number of Guests:</label>
        <input
          type="text"
          inputMode="numeric"
          value={booking.guestNo || ""}
          onChange={(e) =>
            handleChange(
              "guestNo",
              e.target.value === "" ? "" : Number(e.target.value),
            )
          }
        />
        <label>Event Inclusions:</label>
        <textarea
          value={eventInclusionsText}
          onChange={(e) => {
            setEventInclusionsText(e.target.value);
          }}
          onBlur={() => {
            const parsed = eventInclusionsText
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean);

            handleChange("eventInclusions", parsed);
          }}
          placeholder="One item per line"
        />
        <label>Inclusions Cost:</label>
        <input
          type="text"
          inputMode="numeric"
          value={inclusionsCostDisplay}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, "");
            const val = raw === "" ? 0 : Number(raw);
            handleChange("inclusionsCost", val);
          }}
        />
        <label>Additions</label>
        <textarea
          value={additionsText}
          onChange={(e) => {
            setAdditionsText(e.target.value);
          }}
          onBlur={() => {
            const parsed = additionsText
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean) // drop only genuinely empty lines
              .map((line, i) => {
                const [leftRaw, priceRaw = "0"] = line.split("@");
                const [nameRaw, quantityRaw = "1"] = (leftRaw || "").split(":");

                // If a line has no name at all, it's not a usable row — skip it.
                // But anything with at least a name survives, even if qty/price
                // weren't formatted exactly right, instead of vanishing outright.
                if (!nameRaw?.trim()) return null;

                return {
                  id:
                    booking.additions?.[i]?.id ||
                    booking.additions?.[i]?._id ||
                    Date.now() + i,
                  name: nameRaw.trim(),
                  quantity: Number(quantityRaw.trim() || 1),
                  price: Number(priceRaw.trim() || 0),
                };
              })
              .filter(Boolean);
            handleChange("additions", parsed);
          }}
        />
        <label>Additions Cost:</label>
        <input
          type="text"
          inputMode="numeric"
          value={additionsCostDisplay}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, "");
            const val = raw === "" ? 0 : Number(raw);
            handleChange("additionsCost", val);
          }}
        />
        <label>Total:</label>
        <input type="number" value={booking.totalCost} disabled />
        <label>Paid:</label>
        <input
          type="text"
          inputMode="numeric"
          value={paidCostDisplay}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, "");
            const val = raw === "" ? 0 : Number(raw);
            handleChange("paidCost", val);
          }}
        />
        <label>Balance:</label>
        <input type="number" value={booking.balanceCost} disabled />
        <label>Additional Notes:</label>{" "}
        <textarea
          value={additionalNotesText || []}
          onChange={(e) => {
            setAdditionalNotesText(e.target.value);
          }}
          onBlur={() => {
            const parsed = additionalNotesText
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean);

            handleChange("additionalNotes", parsed);
          }}
          placeholder="One item per line"
        />
      </div>
      <div className="bookingDetail__buttons">
        <button
          type="button"
          className="bookingDetail__button my-btn"
          onClick={handleUpdate}
        >
          Save Updates
        </button>
        <button
          type="button"
          className="bookingDetail__button my-btn"
          onClick={handlePrintInvoice}
        >
          Print Booking PDF
        </button>
        <button
          type="button"
          className="bookingDetail__button my-btn"
          onClick={handleCreateInvoice}
        >
          Create Invoice
        </button>

        <button
          type="button"
          className="bookingDetail__button my-btn"
          onClick={handleDelete}
        >
          Delete Booking
        </button>
      </div>
    </div>
  );
}
